
export function NFCRedirectSection() {
  return (
    <section className="relative py-16 lg:py-32 bg-[#0f0f0f] overflow-hidden">
      {/* Glitch Decoration at Top */}
      <div className="absolute top-0 left-0 w-full h-1 flex gap-2">
        <div className="flex-1 bg-neon-pink" style={{ boxShadow: '0 0 10px #ff006e' }} />
        <div className="flex-1 bg-neon-green" style={{ boxShadow: '0 0 10px #00ff41' }} />
        <div className="flex-1 bg-neon-blue" style={{ boxShadow: '0 0 10px #00d9ff' }} />
      </div>

      <div className="container mx-auto px-4 sm:px-8 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Section Title */}
          <div className="text-center mb-10 lg:mb-20">
            <h2 className="font-display text-3xl sm:text-5xl lg:text-7xl text-white mb-3 lg:mb-6 tracking-wider">
              NFC REDIRECT ENGINE
            </h2>
            <p
              className="text-neon-green text-sm lg:text-2xl font-light tracking-wide mb-4 lg:mb-8"
              style={{ textShadow: '0 0 20px #00ff41' }}
            >
              1つの共通URLから、地域別のアーティストをランダムに呼び出す
            </p>
            <p className="text-gray-400 text-xs lg:text-lg max-w-3xl mx-auto leading-relaxed">
              街中に配置されたNFCタグは、すべて同じURLを共有。<br className="hidden sm:block" />
              しかし、あなたの位置情報に基づいて、その地域で活動する
              <strong className="text-white">ランダムなアーティスト</strong>
              が表示される。
            </p>
          </div>

          {/* Visual Diagram */}
          <div className="relative mb-12 lg:mb-20">
            {/* Center NFC Tag */}
            <div className="flex justify-center mb-10 lg:mb-16">
              <div className="relative group cursor-pointer">
                <div className="absolute inset-0 bg-neon-pink/30 rounded-full blur-3xl animate-pulse" />
                <div
                  className="relative w-28 h-28 lg:w-48 lg:h-48 rounded-full bg-gradient-to-br from-[#2a2a2a] to-[#1a1a1a] border-4 border-neon-pink flex items-center justify-center"
                  style={{ boxShadow: '0 0 60px rgba(255, 0, 110, 0.6)' }}
                >
                  <i className="ri-nfc-line text-4xl lg:text-7xl text-neon-pink"></i>
                </div>
                <div className="absolute -bottom-10 lg:-bottom-12 left-1/2 -translate-x-1/2 whitespace-nowrap">
                  <p className="text-white font-bold text-sm lg:text-lg">共通NFCタグ</p>
                  <p className="text-gray-500 text-[10px] lg:text-sm text-center">crash.music/nfc</p>
                </div>
              </div>
            </div>

            {/* Arrows and Artist Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-8 mt-16 lg:mt-24">
              {[
                { location: 'TOKYO', color: '#00ff41', icon: 'ri-map-pin-line' },
                { location: 'OSAKA', color: '#ff006e', icon: 'ri-map-pin-line' },
                { location: 'KYOTO', color: '#00d9ff', icon: 'ri-map-pin-line' },
                { location: 'FUKUOKA', color: '#b537f2', icon: 'ri-map-pin-line' },
              ].map((region, index) => (
                <div key={index} className="relative">
                  {/* Arrow from center */}
                  <div
                    className="absolute -top-12 lg:-top-20 left-1/2 -translate-x-1/2 w-0.5 h-8 lg:h-16 opacity-50"
                    style={{
                      background: `linear-gradient(to bottom, ${region.color}, transparent)`,
                      boxShadow: `0 0 10px ${region.color}`,
                    }}
                  />
                  {/* Region Card */}
                  <div
                    className="bg-black/80 backdrop-blur-sm rounded-xl lg:rounded-2xl p-4 lg:p-6 border-2 hover:scale-105 transition-all duration-300 cursor-pointer"
                    style={{
                      borderColor: region.color,
                      boxShadow: `0 0 30px ${region.color}40`,
                    }}
                  >
                    <div
                      className="w-10 h-10 lg:w-12 lg:h-12 rounded-full flex items-center justify-center mb-3 lg:mb-4 mx-auto"
                      style={{
                        background: `${region.color}20`,
                        border: `2px solid ${region.color}`,
                        boxShadow: `0 0 20px ${region.color}60`,
                      }}
                    >
                      <i className={`${region.icon} text-lg lg:text-2xl`} style={{ color: region.color }}></i>
                    </div>

                    <h4 className="text-white text-sm lg:text-xl font-bold text-center mb-1 lg:mb-2">
                      {region.location}
                    </h4>
                    <p className="text-gray-400 text-[10px] lg:text-sm text-center">地域のアーティスト</p>

                    <div className="mt-3 lg:mt-4 pt-3 lg:pt-4 border-t border-white/10">
                      <p className="text-gray-500 text-[10px] lg:text-xs text-center">ランダム表示</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Features List */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-8 max-w-4xl mx-auto">
            <div className="bg-black/50 backdrop-blur-sm rounded-xl lg:rounded-2xl p-5 lg:p-8 border border-white/10">
              <div className="flex items-start gap-3 lg:gap-4">
                <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-lg bg-neon-green/20 border border-neon-green flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-2-line text-lg lg:text-xl text-neon-green"></i>
                </div>
                <div>
                  <h4 className="text-white text-sm lg:text-lg font-bold mb-1 lg:mb-2">位置情報ベース</h4>
                  <p className="text-gray-400 text-xs lg:text-sm leading-relaxed">
                    あなたの現在地を検出し、その地域で活動するアーティストのみを表示。
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-black/50 backdrop-blur-sm rounded-xl lg:rounded-2xl p-5 lg:p-8 border border-white/10">
              <div className="flex items-start gap-3 lg:gap-4">
                <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-lg bg-neon-blue/20 border border-neon-blue flex items-center justify-center flex-shrink-0">
                  <i className="ri-link text-lg lg:text-xl text-neon-blue"></i>
                </div>
                <div>
                  <h4 className="text-white text-sm lg:text-lg font-bold mb-1 lg:mb-2">共通URL管理</h4>
                  <p className="text-gray-400 text-xs lg:text-sm leading-relaxed">
                    すべてのNFCタグは1つのURLを共有。管理が簡単で大量配布が可能。
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-black/50 backdrop-blur-sm rounded-xl lg:rounded-2xl p-5 lg:p-8 border border-white/10">
              <div className="flex items-start gap-3 lg:gap-4">
                <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-lg bg-neon-purple/20 border border-neon-purple flex items-center justify-center flex-shrink-0">
                  <i className="ri-database-2-line text-lg lg:text-xl text-neon-purple"></i>
                </div>
                <div>
                  <h4 className="text-white text-sm lg:text-lg font-bold mb-1 lg:mb-2">リアルタイム更新</h4>
                  <p className="text-gray-400 text-xs lg:text-sm leading-relaxed">
                    新しいアーティストが登録されると、即座にランダム表示の対象に。
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Tech Stack */}
          <div className="mt-12 lg:mt-20 text-center">
            <p className="text-gray-500 text-xs lg:text-sm tracking-widest mb-4 lg:mb-6 uppercase">Powered By</p>
            <div className="flex justify-center gap-3 lg:gap-8 flex-wrap">
              {['NFC Technology', 'Geolocation API', 'Random Algorithm', 'Cloud Database'].map(
                (tech, index) => (
                  <div
                    key={index}
                    className="px-4 lg:px-6 py-2 lg:py-3 bg-black/50 backdrop-blur-sm rounded-full border border-white/10 text-gray-400 text-[10px] lg:text-sm font-light hover:border-neon-green hover:text-neon-green transition-all duration-300 cursor-pointer"
                  >
                    {tech}
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
